import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { notifyError, notifySuccess } from '../../utils/notifications';
import { AlertInfoPanel } from './AlertInfoPanel';
import { AlertLevelsCard } from './AlertLevelsCard';
import { AudienceCard } from './AudienceCard';
function formatLevelText(level) {
    const periodMap = {
        ThirtyDays: '30 days',
        SevenDays: '7 days',
        CalendarMonth: 'calendar month',
    };
    return `${level.NcDays} nc in any ${periodMap[level.InPeriod] ?? ''}`;
}
export function AlertSettingsPage() {
    // Level settings state
    const [weeklySummaryReportDay, setWeeklySummaryReportDay] = useState('Monday');
    const [percentageBasedLevels, setPercentageBasedLevels] = useState({
        VesselNcToleranceLevel: 0,
        RankNcToleranceLevel: 0,
    });
    const [ncCountBasedLevels, setNcCountBasedLevels] = useState([]);
    const [consecutiveSixOnOffDays, setConsecutiveSixOnOffDays] = useState(2);
    const [usePercentageLevels, setUsePercentageLevels] = useState(false);
    // Audience state
    const [vesselAlert, setVesselAlert] = useState(null);
    const [accountHolder, setAccountHolder] = useState(null);
    const [additionalRecipients, setAdditionalRecipients] = useState([]);
    const [availableFleets, setAvailableFleets] = useState([]);
    // Per-section dirty tracking
    const [hasLevelChanges, setHasLevelChanges] = useState(false);
    const [hasAudienceChanges, setHasAudienceChanges] = useState(false);
    const initialLoadDone = useRef(false);
    const markLevelsDirty = useCallback(() => {
        if (initialLoadDone.current)
            setHasLevelChanges(true);
    }, []);
    const markAudienceDirty = useCallback(() => {
        if (initialLoadDone.current)
            setHasAudienceChanges(true);
    }, []);
    // Wrap setters to mark dirty
    const handleWeeklySummaryDayChange = useCallback((day) => {
        setWeeklySummaryReportDay(day);
        markLevelsDirty();
    }, [markLevelsDirty]);
    const handleUsePercentageLevelsChange = useCallback((value) => {
        setUsePercentageLevels(value);
        markLevelsDirty();
    }, [markLevelsDirty]);
    const handlePercentageLevelsChange = useCallback((levels) => {
        setPercentageBasedLevels(levels);
        markLevelsDirty();
    }, [markLevelsDirty]);
    const handleConsecutiveDaysChange = useCallback((days) => {
        setConsecutiveSixOnOffDays(days);
        markLevelsDirty();
    }, [markLevelsDirty]);
    const handleVesselAlertChange = useCallback((data) => {
        setVesselAlert(data);
        markAudienceDirty();
    }, [markAudienceDirty]);
    const handleAccountHolderChange = useCallback((data) => {
        setAccountHolder(data);
        markAudienceDirty();
    }, [markAudienceDirty]);
    const handleAdditionalRecipientsChange = useCallback((data) => {
        setAdditionalRecipients(data);
        markAudienceDirty();
    }, [markAudienceDirty]);
    // beforeunload
    useEffect(() => {
        if (!hasLevelChanges && !hasAudienceChanges)
            return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = true;
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasLevelChanges, hasAudienceChanges]);
    // Load data
    useEffect(() => {
        const load = async () => {
            const [fleetsRes, settingsRes] = await Promise.all([
                fetch('/api/lists/fleets'),
                fetch('/api/settings'),
            ]);
            if (fleetsRes.status !== 200 || settingsRes.status !== 200) {
                notifyError('An error has occurred. Please try again');
                return;
            }
            const fleets = await fleetsRes.json();
            const data = await settingsRes.json();
            setAvailableFleets(fleets);
            setPercentageBasedLevels(data.AlertLevelSettings.PercentageBasedLevels);
            setWeeklySummaryReportDay(data.AlertLevelSettings.WeeklySummaryReportDay);
            setConsecutiveSixOnOffDays(data.AlertLevelSettings.ConsecutiveSixOnOffDays);
            const ncLevels = [];
            for (const companyId in data.AlertLevelSettings.NcCountBasedLevels) {
                const levels = data.AlertLevelSettings.NcCountBasedLevels[companyId];
                ncLevels.push({
                    Company: data.CompanyNames[companyId] ?? '',
                    Level1: formatLevelText(levels.Level1),
                    Level2: formatLevelText(levels.Level2),
                    Level3: formatLevelText(levels.Level3),
                    CompanyLevels: levels,
                });
            }
            setNcCountBasedLevels(ncLevels);
            if (data.AlertsAudience.VesselAlert != null) {
                setVesselAlert(data.AlertsAudience.VesselAlert);
            }
            setAccountHolder(data.AlertsAudience.AccountHolder);
            setAdditionalRecipients(data.AlertsAudience.AdditionalRecipients);
            setUsePercentageLevels(data.AlertLevelSettings.UsePercentageLevels);
            // Mark initial load complete (defer to avoid flagging dirty during load)
            requestAnimationFrame(() => {
                initialLoadDone.current = true;
            });
        };
        load();
    }, []);
    // Save level settings
    const saveLevelsSettings = useCallback(async () => {
        const response = await fetch('/api/settings/levels', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                PercentageBasedLevels: percentageBasedLevels,
                WeeklySummaryReportDay: weeklySummaryReportDay,
                ConsecutiveSixOnOffDays: consecutiveSixOnOffDays,
                UsePercentageLevels: usePercentageLevels,
            }),
        });
        if (response.ok) {
            setHasLevelChanges(false);
            notifySuccess('Settings successfully saved');
        }
        else {
            notifyError('An error has occurred when saving the alerts settings. Please try again');
        }
    }, [percentageBasedLevels, weeklySummaryReportDay, consecutiveSixOnOffDays, usePercentageLevels]);
    // Save audience settings
    const saveAudienceSettings = useCallback(async () => {
        const response = await fetch('/api/settings/audience', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                VesselAlert: vesselAlert,
                AccountHolder: accountHolder,
                AdditionalRecipients: additionalRecipients,
            }),
        });
        if (response.ok) {
            setHasAudienceChanges(false);
            notifySuccess('Settings successfully saved');
        }
        else {
            notifyError('An error has occurred when saving the audience settings. Please try again');
        }
    }, [vesselAlert, accountHolder, additionalRecipients]);
    return (_jsxs("div", { id: "alertSettingsConfigContainer", className: "row d-lg-block clearfix", children: [_jsx(AlertLevelsCard, { weeklySummaryReportDay: weeklySummaryReportDay, onWeeklySummaryReportDayChange: handleWeeklySummaryDayChange, usePercentageLevels: usePercentageLevels, onUsePercentageLevelsChange: handleUsePercentageLevelsChange, percentageBasedLevels: percentageBasedLevels, onPercentageBasedLevelsChange: handlePercentageLevelsChange, ncCountBasedLevels: ncCountBasedLevels, consecutiveSixOnOffDays: consecutiveSixOnOffDays, onConsecutiveSixOnOffDaysChange: handleConsecutiveDaysChange, onSave: saveLevelsSettings }), _jsx(AlertInfoPanel, {}), _jsx(AudienceCard, { usePercentageLevels: usePercentageLevels, vesselAlert: vesselAlert, accountHolder: accountHolder, additionalRecipients: additionalRecipients, availableFleets: availableFleets, onVesselAlertChange: handleVesselAlertChange, onAccountHolderChange: handleAccountHolderChange, onAdditionalRecipientsChange: handleAdditionalRecipientsChange, onSave: saveAudienceSettings })] }));
}
